// components/FormOptionsDrawer.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  Slider,
  Switch,
  FormControlLabel,
  FormControl,
  Button,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import { CloudSync as MCPServerIcon } from '@mui/icons-material';
import axiosClient, { pythonClient } from '../api/axios.client';
import MCPServerManager from './MCP/MCPServerManager';
import mcpService from '../services/mcpService';
import UsageIndicator from './LLMUsage/UsageIndicator';
import { OLLAMA_MODEL_OPTIONS, DEFAULT_OLLAMA_MODEL } from '../constants/ollamaModels.js';

const drawerWidth = 320;

function FormOptionsDrawer({
  open,
  onClose,
  selectedModel,
  setSelectedModel,
  temperature,
  setTemperature,
  playAudio,
  handlePlayAudioChange,
  darkMode,
  onToggleTheme,
  selectedFlow,
  setSelectedFlow,
  selectedMCPServer,
  setSelectedMCPServer,
  selectedMCPTool,
  setSelectedMCPTool,
}) {
  let theme;
  let isMobile;

  try {
    theme = useTheme();
    isMobile = useMediaQuery ? useMediaQuery(theme.breakpoints.down('md')) : false;
    // Defensive: ensure theme.palette.divider exists for test environments
    if (!theme.palette) {
      theme.palette = { divider: '#e0e0e0' };
    } else if (!theme.palette.divider) {
      theme.palette.divider = '#e0e0e0';
    }
  } catch (e) {
    theme = { breakpoints: { down: () => false }, palette: { divider: '#e0e0e0' } };
    isMobile = false;
  }

  // Dynamically load available models from tenant LLM keys
  const [availableModels, setAvailableModels] = useState([]);
  const [availableFlows, setAvailableFlows] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [flowAccordionExpanded, setFlowAccordionExpanded] = useState(!!selectedFlow);
  const [modelAccordionExpanded, setModelAccordionExpanded] = useState(!selectedFlow);

  // MCP-related state
  const [mcpServers, setMcpServers] = useState([]);
  const [mcpServerManagerOpen, setMcpServerManagerOpen] = useState(false);
  const [mcpTools, setMcpTools] = useState([]);
  const [mcpToolsLoading, setMcpToolsLoading] = useState(false);
  const [mcpAccordionExpanded, setMcpAccordionExpanded] = useState(false);

  useEffect(() => {
    async function fetchTenantModels() {
      try {
        // Get tenant_id from localStorage or another reliable source
        const tenantId = localStorage.getItem('tenant_id');
        if (!tenantId) {
          setAvailableModels([]);
          return;
        }
        const response = await axiosClient.get(`tenants/${tenantId}/llm-keys`);
        const data = response;
        // Same model list as NodeConfigDialog - Gemini models when tenant has gemini key
        const models = [
          { value: 'openai', label: 'OpenAI', provider: 'openai' },
          { value: 'llama4', label: 'LLaMA 4', provider: 'llama-api' },
          ...OLLAMA_MODEL_OPTIONS,
          { value: 'deepseek', label: 'DeepSeek', provider: 'deepseek' },
          { value: 'mixtral', label: 'Mixtral', provider: 'mistral' },
          { value: 'openaa', label: 'OpenAA', provider: 'openaa' },
          { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite', provider: 'gemini' },
          { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', provider: 'gemini' },
          { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', provider: 'gemini' },
          { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', provider: 'gemini' },
          { value: 'gemini-pro', label: 'Gemini Pro', provider: 'gemini' },
          { value: 'anthropic', label: 'Anthropic', provider: 'anthropic' },
          { value: 'azure', label: 'Azure', provider: 'azure' },
          { value: 'mistral', label: 'Mistral', provider: 'mistral' },
          { value: 'cohere', label: 'Cohere', provider: 'cohere' },
          { value: 'together', label: 'Together', provider: 'together' },
          { value: 'custom', label: 'Custom', provider: 'custom' },
        ].filter(
          (m) => m.provider === 'ollama' // Always include Ollama
            || (m.provider === 'gemini' && (data.gemini || data.gemini_url)) // Gemini when tenant has key
            || data[m.value]
            || data[`${m.value}_url`],
        );
        setAvailableModels(models);
      } catch (error) {
        console.error('Error fetching tenant models:', error);
        setAvailableModels([]);
      }
    }
    if (open) fetchTenantModels();
  }, [open]);

  useEffect(() => {
    async function fetchFlows() {
      setFlowsLoading(true);
      try {
        const response = await axiosClient.get('flows');

        // Handle different response structures
        // axiosClient interceptor extracts response.data automatically
        if (response?.flows) {
          setAvailableFlows(response.flows);
        } else if (Array.isArray(response)) {
          setAvailableFlows(response);
        } else {
          console.warn('Unexpected response structure:', response);
          setAvailableFlows([]);
        }
      } catch (error) {
        console.error('Error fetching flows:', error);
        setAvailableFlows([]);
      } finally {
        setFlowsLoading(false);
      }
    }
    if (open) fetchFlows();
  }, [open]);

  // Update accordion expansion states when selectedFlow or MCP server changes
  useEffect(() => {
    setFlowAccordionExpanded(!!selectedFlow);
    setModelAccordionExpanded(!selectedFlow && !selectedMCPServer);
    setMcpAccordionExpanded(!!selectedMCPServer);
  }, [selectedFlow, selectedMCPServer]);

  // Load MCP servers on drawer open
  useEffect(() => {
    if (open) {
      const servers = mcpService.getServers();
      setMcpServers(servers);
      // Expand MCP section if there are servers or if MCP server is selected
      setMcpAccordionExpanded(servers.length > 0 || !!selectedMCPServer);
    }
  }, [open, selectedMCPServer]);

  // Load MCP tools when server is selected
  useEffect(() => {
    if (selectedMCPServer && open) {
      loadMCPTools(selectedMCPServer.id);
    }
  }, [selectedMCPServer, open]);

  const models = [
    { value: 'llama4', label: 'LLaMA 4 Preview' },
    ...OLLAMA_MODEL_OPTIONS.map(({ value, label }) => ({ value, label })),
    { value: 'deepseek-v2', label: 'DeepSeek V2' },
  ];

  const quickPrompts = [
    'Explain this concept in simple terms',
    'Write a summary of this topic',
    'Create a step-by-step guide',
    'Provide examples and use cases',
    'Compare and contrast different approaches',
    'Help me debug this code',
    'Translate this to another language',
    'Make this more professional',
  ];

  const handleQuickPrompt = (prompt) => {
    setQuestion(prompt);
    onClose();
  };

  const handleFlowSelect = async (flowId) => {
    console.log('Flow selected:', flowId);
    if (!flowId) {
      console.log('Clearing flow selection');
      setSelectedFlow(null);
      setSelectedMCPServer(null);
      setSelectedMCPTool(null);
      return;
    }
    try {
      console.log('Fetching flow details for:', flowId);
      const response = await axiosClient.get(`flows/${flowId}`);
      console.log('Flow response:', response);
      // axiosClient interceptor extracts response.data automatically
      if (response?.flow) {
        console.log('Setting selected flow:', response.flow);
        setSelectedFlow(response.flow);
        setSelectedModel(null);
        setSelectedMCPServer(null);
        setSelectedMCPTool(null);
      } else {
        console.log('No flow data in response, clearing selection');
        setSelectedFlow(null);
      }
    } catch (error) {
      console.error('Error fetching flow:', error);
      setSelectedFlow(null);
    }
  };

  const handleMCPServerSelect = async (serverId) => {
    console.log('MCP Server selected:', serverId);
    if (!serverId) {
      console.log('Clearing MCP server selection');
      setSelectedMCPServer(null);
      setSelectedMCPTool(null);
      setSelectedFlow(null);
      setSelectedModel(null);
      return;
    }

    const server = mcpServers.find((s) => s.id === serverId);
    if (server) {
      setSelectedMCPServer(server);
      setSelectedFlow(null);
      setSelectedModel(null);
      await loadMCPTools(serverId);
    }
  };

  const handleMCPToolSelect = (tool) => {
    console.log('MCP Tool selected:', tool);
    setSelectedMCPTool(tool);
  };

  const loadMCPTools = async (serverId) => {
    setMcpToolsLoading(true);
    try {
      const tools = await mcpService.getAvailableTools(serverId);
      setMcpTools(tools);
    } catch (error) {
      console.error('Error loading MCP tools:', error);
      setMcpTools([]);
    } finally {
      setMcpToolsLoading(false);
    }
  };

  const handleOpenMCPServerManager = () => {
    setMcpServerManagerOpen(true);
  };

  const handleMCPServerManagerClose = () => {
    setMcpServerManagerOpen(false);
    // Refresh servers list
    const servers = mcpService.getServers();
    setMcpServers(servers);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ width: drawerWidth, flexShrink: 0 }}
      PaperProps={{ sx: { width: drawerWidth } }}
    >
      <Box sx={{
        p: 2, height: '100%', display: 'flex', flexDirection: 'column',
      }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Chat Options</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={3}>
          {/* MCP Server Selection - Always visible */}
          <Accordion
            expanded={mcpAccordionExpanded}
            onChange={() => setMcpAccordionExpanded(!mcpAccordionExpanded)}
            defaultExpanded
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                MCP Server
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {mcpServers.length === 0 && (
                  <Box sx={{
                    p: 2,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    textAlign: 'center',
                  }}
                  >
                    <MCPServerIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      No MCP servers configured
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Connect to external AI agents and flows via MCP protocol
                    </Typography>
                  </Box>
                )}

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <FormControl fullWidth>
                    <InputLabel>Select MCP Server</InputLabel>
                    <Select
                      value={selectedMCPServer?.id || ''}
                      onChange={(e) => handleMCPServerSelect(e.target.value)}
                      label="Select MCP Server"
                      displayEmpty
                    >
                      <MenuItem key="none" value=""><em>None</em></MenuItem>
                      {mcpServers.map((server) => (
                        <MenuItem key={server.id} value={server.id}>
                          {server.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    onClick={handleOpenMCPServerManager}
                    size="small"
                    sx={{ ml: 1 }}
                    title="Manage MCP Servers"
                  >
                    <MCPServerIcon />
                  </IconButton>
                </Box>

                {selectedMCPServer && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedMCPServer.description || `${selectedMCPServer.host}:${selectedMCPServer.port}`}
                  </Typography>
                )}

                {selectedMCPServer && (
                  <FormControl fullWidth>
                    <InputLabel>Select MCP Tool</InputLabel>
                    <Select
                      value={selectedMCPTool?.name || ''}
                      onChange={(e) => {
                        const tool = mcpTools.find((t) => t.name === e.target.value);
                        handleMCPToolSelect(tool);
                      }}
                      label="Select MCP Tool"
                      disabled={mcpToolsLoading}
                      displayEmpty
                    >
                      <MenuItem key="none" value=""><em>None</em></MenuItem>
                      {mcpTools.map((tool) => (
                        <MenuItem key={tool.name} value={tool.name}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {tool.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {tool.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {selectedMCPTool && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedMCPTool.description}
                  </Typography>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Flow Selection */}
          <Accordion
            expanded={flowAccordionExpanded}
            onChange={() => setFlowAccordionExpanded(!flowAccordionExpanded)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Agentic Flow
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth>
                <InputLabel>Select Flow</InputLabel>
                <Select
                  value={selectedFlow ? (selectedFlow._id || selectedFlow.id || selectedFlow.flowId || '') : ''}
                  onChange={(e) => handleFlowSelect(e.target.value)}
                  label="Select Flow"
                  disabled={flowsLoading}
                  displayEmpty
                >
                  <MenuItem key="none" value=""><em>None</em></MenuItem>
                  {availableFlows.map((flow) => {
                    const flowId = flow._id || flow.id || flow.flowId;
                    const key = `flow-${flowId || Math.random()}`;
                    return (
                      <MenuItem key={key} value={flowId || ''}>
                        {flow.name || 'Unnamed Flow'}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {selectedFlow && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedFlow.description}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Model Selection (disabled if flow is selected) */}
          <Accordion
            expanded={modelAccordionExpanded}
            onChange={() => setModelAccordionExpanded(!modelAccordionExpanded)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                AI Model
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth>
                <InputLabel>Select Model</InputLabel>
                <Select
                  value={selectedModel || ''}
                  onChange={(e) => {
                    setSelectedModel(e.target.value || null);
                    setSelectedFlow(null);
                    setSelectedMCPServer(null);
                    setSelectedMCPTool(null);
                  }}
                  label="Select Model"
                  disabled={!!selectedFlow || !!selectedMCPServer}
                  displayEmpty
                >
                  {availableModels.length === 0 && (
                    <MenuItem key="no-models" value="" disabled>
                      No models configured for this tenant
                    </MenuItem>
                  )}
                  {availableModels.map((m) => (
                    <MenuItem key={`model-${m.value || Math.random()}`} value={m.value || ''}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {m.label || 'Unnamed Model'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Usage Indicator for selected model */}
              {selectedModel && (
                <Box sx={{ mt: 2 }}>
                  <UsageIndicator provider={null} model={selectedModel} darkMode={darkMode} compact={false} />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Temperature Control */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Temperature
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Slider
                value={temperature}
                onChange={(_, value) => setTemperature(value)}
                min={0}
                max={2}
                step={0.1}
                marks={[
                  { value: 0, label: 'Low' },
                  { value: 0.7, label: 'Mid' },
                  { value: 2, label: 'High' },
                ]}
                valueLabelDisplay="auto"
                disabled={!!selectedFlow}
              />
            </AccordionDetails>
          </Accordion>

          {/* Audio Settings */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Audio & Voice
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={playAudio}
                      onChange={handlePlayAudioChange}
                      color="primary"
                    />
                    )}
                  label="Text-to-Speech"
                />
                <Typography variant="caption" color="text.secondary">
                  Enable voice responses and speech recognition
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Appearance */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Appearance
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={darkMode}
                      onChange={onToggleTheme}
                      color="primary"
                    />
                    )}
                  label={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {darkMode ? <DarkModeIcon /> : <LightModeIcon />}
                      Dark Mode
                    </Box>
                    )}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Export Options */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Data & Export
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    // Trigger export chat event
                    window.dispatchEvent(new CustomEvent('exportChat'));
                    onClose();
                  }}
                  fullWidth
                >
                  Export Current Chat
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<ClearIcon />}
                  onClick={() => {
                    // Trigger clear history event
                    window.dispatchEvent(new CustomEvent('clearHistory'));
                    onClose();
                  }}
                  fullWidth
                >
                  Clear Chat History
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Box>

      {/* MCP Server Manager */}
      <MCPServerManager
        open={mcpServerManagerOpen}
        onClose={handleMCPServerManagerClose}
        onServerSelect={handleMCPServerSelect}
        selectedServer={selectedMCPServer}
        onToolSelect={handleMCPToolSelect}
        selectedTool={selectedMCPTool}
      />
    </Drawer>
  );
}

export default FormOptionsDrawer;
